import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import {getSelectedListItems} from '@Components/list/list-reducer';
import {getUniqueString} from '@Utils/string.util';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';
import type {OnLanguageChangeHandler} from '@Components/language-selector/language-selector.types';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {onAjaxError} from '@Utils/ajax.util';
import {loadLanguages} from './language-library';
import {Dropdown} from '../dropdown-v2';
import type {DropdownPopupProperties} from '../dropdown-v2/dropdown.types';
import {ALIGNMENT_TYPE} from '../dropdown-v2/dropdown.types';
import {useAppSelector} from '@/hooks';
import {List} from '../list';
import {LIST_ITEM_TYPE} from '../list/list.types';
import type {ListItemType3Storage} from '../list/components/list-item-type-3';
import {CircularProgressLoader} from '@Components/circular-progress-loader';

interface LanguageSelectorProps {
  onClick?: OnLanguageChangeHandler;
  className?: string;
  popupClassName?: string;
  showDefaultTypeSelectorForDropdown?: boolean;
  hasIcon?: boolean;
  langDropdownPopupProperties?: DropdownPopupProperties;
  showListOnly?: boolean;
  showInModalForSmallScreen?: boolean;
}

const LIST_ITEM_ID_PREFIX = 'item-';

export function LanguageSelector({
  onClick = $.noop,
  showDefaultTypeSelectorForDropdown = false,
  className = '',
  popupClassName = '',
  hasIcon = false,
  langDropdownPopupProperties = {},
  showListOnly = false,
  showInModalForSmallScreen = false,
}: LanguageSelectorProps) {
  const langListId = useRef(getUniqueString());
  const langData = useAppSelector((state) => {
    return state.langSelector.pmwLangs;
  });
  const selectedItems = useAppSelector((state) => {
    return getSelectedListItems(state.list, langListId.current);
  });
  const isLoading = langData.length === 0;

  useEffect(() => {
    loadLanguages();
  }, []);

  const getDomainByLangCode = (langCode: string): string => {
    for (const langItem of langData) {
      if (langItem.code === langCode) {
        return langItem.domain;
      }
    }
    return '';
  };

  const getCurrentDomain = (): string => {
    for (const langItem of langData) {
      if (langItem.code === window.i18next.language) {
        return langItem.domain;
      }
    }
    return '';
  };

  const onClickHandler = async (listItemId: string, subItemId: string): Promise<void> => {
    if (subItemId !== LIST_ITEM_ID_PREFIX + window.i18next.language) {
      const newLangCode = subItemId.replace(LIST_ITEM_ID_PREFIX, '');
      await onClick();
      await changeLanguage(newLangCode);
    }
  };

  const changeLanguage = async (newLangCode: string): Promise<void> => {
    try {
      await window.PMW.writeLocal('user/setUserLanguage', {
        lang: newLangCode,
      });
      window.PMW.util.changeLanguage(newLangCode, getCurrentDomain(), getDomainByLangCode(newLangCode));
    } catch (e) {
      onAjaxError(e);
    }
  };

  const getListItems = () => {
    if (langData) {
      const dropdownList: Array<ListItemType3Storage> = [];
      for (const langItem of langData) {
        dropdownList.push({
          id: LIST_ITEM_ID_PREFIX + langItem.code,
          text: langItem.displayName,
          type: LIST_ITEM_TYPE.DEFAULT_3,
          isSelected: langItem.code === window.i18next.language,
          onClick: onClickHandler,
        });
      }
      return dropdownList;
    }
    return [];
  };

  const getCurrentLanguageDisplayName = (): string => {
    if (!langData) {
      return '';
    }

    return (
      langData.find((langItem) => {
        return langItem.code === window.i18next.language;
      })?.displayName ?? ''
    );
  };

  const getDropdownSelectorText = (): string => {
    if (isLoading) {
      return window.i18next.t('pmwjs_loading');
    }

    return selectedItems && selectedItems[0] ? selectedItems[0].text : getCurrentLanguageDisplayName();
  };

  const getLangSelectBox = (): ReactElement<any> => {
    const text = getDropdownSelectorText();
    const DropdownSelectBox = showDefaultTypeSelectorForDropdown ? DropdownDefaultSelectBox : DropdownGhostSelectBox;

    return <DropdownSelectBox icon={hasIcon ? 'icon-translation' : ''} text={text} isLoading={isLoading} />;
  };

  const getLanguageList = (): ReactElement<any> => {
    return <List className="spacing-m-0" id={langListId.current} draggable={false} multiSelect={false} keepAtleastOneItemSelected items={getListItems()} />;
  };

  if (showListOnly) {
    return <div className={`_full-height _full-width ${isLoading ? 'flex-center' : ''}`}>{isLoading ? <CircularProgressLoader /> : getLanguageList()}</div>;
  }

  return (
    <Dropdown
      {...langDropdownPopupProperties}
      id="language-selector"
      className={className}
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      popupClassName={popupClassName}
      selector={getLangSelectBox()}
      popup={getLanguageList()}
      showModalForSmallScreen={showInModalForSmallScreen}
      modalTitle={window.i18next.t('pmwjs_language')}
    />
  );
}
